/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Footer from '../components/Footer';
import BulkUploadModal from '../components/BulkUploadModal'; // Import the modal component

function Account() {
    const navigate = useNavigate();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false); // Modal state

    // React Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Advanced filters
    const [filters, setFilters] = useState({
        company_name: '',
        industry: '',
        account_status: '',
        phone: '',
        email: ''
    });

    // Filter accounts based on search term
    const filteredAccounts = accounts.filter(account =>
        account.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.sub_industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.account_status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.website?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply advanced filters if any are set
    const advancedFilteredAccounts = filteredAccounts.filter(account => {
        return (
            (filters.company_name === '' ||
                account.company_name?.toLowerCase().includes(filters.company_name.toLowerCase())) &&
            (filters.industry === '' ||
                account.industry?.toLowerCase().includes(filters.industry.toLowerCase())) &&
            (filters.account_status === '' ||
                account.account_status?.toLowerCase().includes(filters.account_status.toLowerCase())) &&
            (filters.phone === '' ||
                account.phone?.includes(filters.phone)) &&
            (filters.email === '' ||
                account.email?.toLowerCase().includes(filters.email.toLowerCase()))
        );
    });

    // Sort filtered accounts
    const sortedAccounts = React.useMemo(() => {
        let sortableAccounts = [...advancedFilteredAccounts];
        if (sortConfig.key) {
            sortableAccounts.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableAccounts;
    }, [advancedFilteredAccounts, sortConfig]);

    // Calculate pagination
    const totalItems = sortedAccounts.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentAccounts = sortedAccounts.slice(startIndex, endIndex);

    // Handle sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sort icon
    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ? '↑' : '↓';
        }
        return '';
    };

    // Reset pagination when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Fetch Accounts from API
    const fetchAccounts = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get("/accounts/");
            console.log("Accounts response:", response.data);
            setAccounts(response.data.data || response.data);
        } catch (error) {
            console.error("Error fetching Accounts:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to fetch accounts data'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            company_name: '',
            industry: '',
            phone: '',
            email: ''
        });
        setSearchTerm(''); // Also clear the main search term
    };

    // Navigate to Account Details page
    const handleViewDetails = (accountId) => {
        navigate(`/account-details/${accountId}`);
    };

    // Edit Account - Navigate to add-account page with account data for update
    const handleEdit = (account) => {
        console.log("account data:", account)
        navigate('/Add-account', { state: { account, isEdit: true } });
    };

    // Delete Account
    const handleDelete = async (accountId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/accounts/${accountId}`);
                Swal.fire('Deleted!', 'Account has been deleted.', 'success');
                fetchAccounts(); // Refresh the list
            } catch (error) {
                console.error('Error deleting account:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'Failed to delete account'
                });
            }
        }
    };

    // Handle bulk upload success
    const handleBulkUploadSuccess = () => {
        fetchAccounts(); // Refresh the accounts list after successful upload
    };

    // Pagination component
    const Pagination = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                    {filteredAccounts.length !== accounts.length && ` (filtered from ${accounts.length} total entries)`}
                </div>
                <div className="d-flex align-items-center">
                    <nav>
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> First
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> Previous
                                </button>
                            </li>
                            {pageNumbers.map(number => (
                                <li key={number} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(number)}
                                    >
                                        {number}
                                    </button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next <i className="fa fa-angle-double-right ms-2"></i>
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    Last
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Accounts</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <button
                                            className="btn btn-filters w-auto"
                                            onClick={() => $('#filter_inputs').toggle()}
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="bottom"
                                            title="Filter"
                                        >
                                            <span className="me-2">
                                                <img src="assets/img/icons/filter-icon.svg" alt="filter" />
                                            </span>Filter
                                        </button>
                                    </li>
                                    <li>
                                        <div className="dropdown dropdown-action" data-bs-toggle="tooltip" data-bs-placement="top" title="Download">
                                            <a href="#" className="btn-filters" data-bs-toggle="dropdown" aria-expanded="false">
                                                <span><i className="fe fe-download"></i></span>
                                            </a>
                                            <div className="dropdown-menu dropdown-menu-end">
                                                <ul className="d-block">
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);">
                                                            <i className="far fa-file-pdf me-2"></i>PDF
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);">
                                                            <i className="far fa-file-text me-2"></i>CSV
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <a className="btn-filters" href="javascript:void(0);" data-bs-toggle="tooltip" data-bs-placement="bottom" title="Print">
                                            <span><i className="fe fe-printer"></i></span>
                                        </a>
                                    </li>
                                    <li>
                                        <button
                                            className="btn btn-import"
                                            onClick={() => setShowBulkUploadModal(true)}
                                        >
                                            <span><i className="fe fe-check-square me-2"></i>Import Accounts</span>
                                        </button>
                                    </li>
                                    <li>
                                        <a className="btn btn-primary" href="/Add-account">
                                            <i className="fa fa-plus-circle me-2" aria-hidden="true"></i>Add Account
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* /Page Header */}

                    {/* Search Filter */}
                    <div id="filter_inputs" className="card filter-card" style={{ display: 'none' }}>
                        <div className="card-body pb-0">
                            <div className="row">
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Company Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="company_name"
                                            value={filters.company_name}
                                            onChange={handleFilterChange}
                                            placeholder="Search by company name"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Industry</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="industry"
                                            value={filters.industry}
                                            onChange={handleFilterChange}
                                            placeholder="Search by industry"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Phone</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="phone"
                                            value={filters.phone}
                                            onChange={handleFilterChange}
                                            placeholder="Search by phone"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Email</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="email"
                                            value={filters.email}
                                            onChange={handleFilterChange}
                                            placeholder="Search by email"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="input-block mb-3 text-end">
                                        <button
                                            type="button"
                                            className="btn btn-secondary me-2"
                                            onClick={clearFilters}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Search Filter */}

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    {/* Table Controls */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }}
                                                placeholder="Search accounts..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('company_name')}
                                                    >
                                                        Company Name {getSortIcon('company_name')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('industry')}
                                                    >
                                                        Industry {getSortIcon('industry')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('account_status')}
                                                    >
                                                        Account Status {getSortIcon('account_status')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('phone')}
                                                    >
                                                        Phone {getSortIcon('phone')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('email')}
                                                    >
                                                        Email {getSortIcon('email')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('website')}
                                                    >
                                                        Website {getSortIcon('website')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('isactive')}
                                                    >
                                                        Status {getSortIcon('isactive')}
                                                    </th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            <div className="spinner-border" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : currentAccounts.length > 0 ? (
                                                    currentAccounts.map((account, index) => (
                                                        <tr key={account.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>
                                                                <span
                                                                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                                    onClick={() => handleViewDetails(account.id)}
                                                                    title="Click to view details"
                                                                >
                                                                    {account.company_name}
                                                                </span>
                                                            </td>
                                                            <td>{account.industry}</td>
                                                            <td>
                                                                <span className={`badge ${account.account_status === 'Customer' ? 'bg-success' :
                                                                    account.account_status === 'Prospect' ? 'bg-warning' : 'bg-secondary'
                                                                    }`}>
                                                                    {account.account_status || 'Not Set'}
                                                                </span>
                                                            </td>
                                                            <td>{account.phone}</td>
                                                            <td>
                                                                {account.email ? (
                                                                    <a href={`mailto:${account.email}`} className="text-primary">
                                                                        {account.email}
                                                                    </a>
                                                                ) : (
                                                                    'N/A'
                                                                )}
                                                            </td>
                                                            <td>
                                                                {account.website ? (
                                                                    <a
                                                                        href={account.website.startsWith('http') ? account.website : `https://${account.website}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-primary"
                                                                    >
                                                                        {account.website}
                                                                    </a>
                                                                ) : (
                                                                    'N/A'
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${account.isactive ? 'bg-success' : 'bg-danger'}`}>
                                                                    {account.isactive ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                            <td className="d-flex align-items-center">
                                                                <a className="btn-action-icon bg-primary-light me-2"
                                                                    onClick={() => handleViewDetails(account.id)}
                                                                    title="View Details"
                                                                >
                                                                    <i className="fa fa-eye me-1"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-success-light me-2"
                                                                    onClick={() => handleEdit(account)}
                                                                >
                                                                    <i className="fa fa-edit me-1"></i>
                                                                </a>
                                                                <a className="btn-action-icon btn-greys bg-danger-light"
                                                                    onClick={() => handleDelete(account.id)}
                                                                >
                                                                    <i className="fa fa-trash-alt me-1"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            {searchTerm ? "No matching accounts found" : "No accounts available in table"}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Table */}
                </div>
                <Footer />
            </div>

            {/* Bulk Upload Modal */}
            <BulkUploadModal
                isOpen={showBulkUploadModal}
                onClose={() => setShowBulkUploadModal(false)}
                onUploadSuccess={handleBulkUploadSuccess}
            />
        </div>
    )
}

export default Account;